<script setup lang="ts">
import type { Form, FormErrorEvent, FormSubmitEvent, ToastProps } from '#ui/types';
import { useMutation } from '@tanstack/vue-query';
import lodashSnakeCase from 'lodash-es/snakeCase';
import { z } from 'zod';

import {
    LazyContentTermsVexatiousCs,
    LazyContentTermsVexatiousDe,
    LazyContentTermsVexatiousEn,
    LazyContentTermsVexatiousEs,
    LazyContentTermsVexatiousFr,
    LazyContentTermsVexatiousIt,
    LazyContentTermsVexatiousNl,
    LazyContentTermsVexatiousPl,
    LazyContentTermsVexatiousPt,
    LazyContentTermsVexatiousRo,
    LazyContentTermsVexatiousRu,
} from '#components';
import {
    apiUserSignUp,
    BackendErrorCode,
    type BackendErrorEntity,
    type BodyType,
    type ErrorType,
    type UserSignupBodyDto,
    type UserEntity,
} from '~/apiClient';
import { AUTOCOMPLETE_OFF } from '~/constants/form';
import { Locale } from '~i18n/i18nConfig';

// PROPS
const { email } = defineProps({
    email: { type: String, default: '' },
});

// EVENTS
const emit = defineEmits<{
    success: [UserEntity];
    editEmail: [];
}>();

// COMPOSABLES
const { locale, tt } = useTypedI18n();
const { notifyBackendError, notifyError, notifyGenericError } = useNotification();
const { getFormErrors } = useBackendErrors();
const { logError, logInfo } = useLogs();
const { trackSignupFieldClick, trackSignupFormError, trackSignupCreateClick } = useTracking();
const { getToken, formatRequestOptions } = useInvisibleRecaptcha();
const { openRequestPasswordResetModal } = useModals();
const { mutateAsync: mutateSignup, isPending: isSubmitting } = useMutation<
    Awaited<ReturnType<typeof apiUserSignUp>>,
    ErrorType<BackendErrorEntity>,
    { data: BodyType<UserSignupBodyDto>; token: string | null },
    unknown
>({
    mutationFn: ({ data, token }) => apiUserSignUp(data, formatRequestOptions(token)),
});
const { trackSignupStandardFormSubmitted } = useTracking();

// DATA
const formSchema = z
    .object({
        email: z.email(tt('validation.msg.invalid', { field: tt('login.email') })),
        password: zodRequiredString(tt('validation.msg.required', { field: tt('signup.fields.password') })).refine(isSecurePassword, {
            message: tt('validation.msg.passwordWeak'),
        }),
        country: z.object(
            {
                countryCode: z.string(),
                taxIdMandatory: z.boolean().optional(),
            },
            { error: tt('validation.msg.required', { field: tt('signup.fields.country') }) }
        ),
        company: z
            .object({
                tin: z.string().optional().nullable(),
                address: z.string().optional().nullable(),
                businessName: z.string().optional(),
                city: z.string().optional().nullable(),
                countryCode: z.string().optional().nullable(),
                postCode: z.string().optional().nullable(),
                province: z.string().optional().nullable(),
                sdi: z.string().optional().nullable(),
            })
            .optional(),
        termsConditions: z.literal<boolean>(true, {
            error: tt('validation.msg.required', { field: tt('signup.fields.consent') }),
        }),
        vexatiousClauses: z.literal<boolean>(true, {
            error: tt('validation.msg.required', { field: tt('signup.fields.consent') }),
        }),
        privacy: z.literal<boolean>(true, {
            error: tt('validation.msg.required', { field: tt('signup.fields.consent') }),
        }),
    })
    .superRefine(({ country, company }, ctx) => {
        if (!country?.taxIdMandatory || company?.tin) return;

        ctx.addIssue({
            code: 'custom',
            message: tt('validation.msg.required', { field: tt('signup.fields.tin.base') }),
            path: ['company'],
        });
    });
type FormSchema = typeof formSchema;
type FormData = z.infer<FormSchema>;
const formData = reactive<Partial<FormData>>({
    email,
    password: '',
    country: undefined,
    company: undefined,
    termsConditions: false,
    vexatiousClauses: false,
    privacy: false,
});
const form = useTemplateRef<Form<FormSchema>>('form');

const onSubmit = async (event: FormSubmitEvent<FormData>): Promise<void> => {
    let recaptchaToken: string | null = null;

    try {
        recaptchaToken = await getToken();
    } catch {
        notifyError({
            title: tt('validation.msg.recaptcha'),
            description: tt('notifications.tryAgain'),
        });

        return;
    }

    const { data } = event;

    const body: UserSignupBodyDto = {
        email: data.email,
        password: data.password,
        companyData: {
            address: data.company?.address,
            countryCode: data.country.countryCode,
            businessName: data.company?.businessName,
            city: data.company?.city,
            postCode: data.company?.postCode,
            province: data.company?.province,
            tin: data.company?.tin,
            sdi: data.company?.sdi,
        },
        language: localeToLanguage(locale.value),
    };

    mutateSignup({ data: body, token: recaptchaToken })
        .then(user => {
            trackSignupStandardFormSubmitted(user);
            emit('success', user);
        })
        .catch(async (apiError: ErrorType<BackendErrorEntity>) => {
            logError('Error during signup step create', {
                apiError: apiError.data,
                body,
            });

            if (!apiError.data?.error) {
                notifyGenericError();
                trackSignupFormError('null');

                return;
            }

            const { error } = apiError.data;

            const actions: ToastProps['actions'] =
                error.code === BackendErrorCode.SignupUserAlreadyExists
                    ? [{ label: tt('login.forgotPassword'), onClick: () => openRequestPasswordResetModal(), variant: 'link' }]
                    : undefined;
            notifyBackendError({
                error,
                actions,
            });
            trackSignupFormError(lodashSnakeCase(error.code));

            const formErrors = getFormErrors<UserSignupBodyDto, FormData>(apiError, {
                email: 'email',
                password: 'password',
                'companyData.countryCode': 'country',
                'companyData.tin': 'company',
            });
            form.value?.setErrors(formErrors);
            await nextTick();
        });
};

const onError = (errorEvent: FormErrorEvent): void => {
    logInfo('Error before signup step create', {
        formErrors: errorEvent.errors,
        formData,
    });

    errorEvent.errors.forEach(e => {
        trackSignupFormError(lodashSnakeCase(`${e.name}_invalid`));
    });

    if (!errorEvent.errors.length) return;

    scrollToFormError();
};

const vexatiousClausesComponent = computed<Component>(() => {
    const componentsByLocale: Record<Locale, Component> = {
        [Locale.CS]: LazyContentTermsVexatiousCs,
        [Locale.DE]: LazyContentTermsVexatiousDe,
        [Locale.EN]: LazyContentTermsVexatiousEn,
        [Locale.ES]: LazyContentTermsVexatiousEs,
        [Locale.FR]: LazyContentTermsVexatiousFr,
        [Locale.IT]: LazyContentTermsVexatiousIt,
        [Locale.NL]: LazyContentTermsVexatiousNl,
        [Locale.PL]: LazyContentTermsVexatiousPl,
        [Locale.PT]: LazyContentTermsVexatiousPt,
        [Locale.RO]: LazyContentTermsVexatiousRo,
        [Locale.RU]: LazyContentTermsVexatiousRu,
    };

    return componentsByLocale[locale.value];
});
</script>

<template>
    <UForm ref="form" class="flex flex-col items-center" :state="formData" :schema="formSchema" @submit="onSubmit" @error="onError">
        <div class="flex flex-col gap-4">
            <div class="flex w-full flex-col gap-4">
                <UFormField :label="tt('login.email')" name="email">
                    <UInput v-model.trim="formData.email" disabled>
                        <template #trailing>
                            <UButton :label="tt('signup.fields.email.change')" size="sm" variant="ghost" square @click="emit('editEmail')" />
                        </template>
                    </UInput>
                </UFormField>

                <UFormField :label="tt('signup.fields.password')" name="password" required>
                    <template #hint>
                        <BasePasswordHint />
                    </template>
                    <template #default>
                        <BasePasswordInput
                            v-model="formData.password"
                            autocomplete="new-password"
                            :disabled="isSubmitting"
                            @click="trackSignupFieldClick('password')"
                        />
                    </template>
                </UFormField>

                <UFormField :label="tt('signup.fields.country')" name="country" required>
                    <BaseCountrySelect
                        v-model="formData.country"
                        :disabled="isSubmitting"
                        :autocomplete="AUTOCOMPLETE_OFF"
                        @click="trackSignupFieldClick('country')"
                    />
                </UFormField>

                <UFormField :label="tt('signup.fields.tin.base')" name="company" :required="formData.country?.taxIdMandatory">
                    <BaseCompanySuggest
                        v-model="formData.company"
                        :country-code="formData.country?.countryCode"
                        :placeholder="tt('signup.fields.tin.placeholder')"
                        :disabled="isSubmitting"
                        @input-click="trackSignupFieldClick('company')"
                    />
                </UFormField>
            </div>

            <div class="w-full">
                <UFormField name="termsConditions">
                    <UCheckbox
                        v-model="formData.termsConditions"
                        class="my-2 md:my-4"
                        name="termsConditions"
                        aria-required="true"
                        :disabled="isSubmitting"
                        @click="trackSignupFieldClick('terms_conditions')"
                    >
                        <template #label>
                            <BaseI18nT
                                tag="span"
                                class="after:text-error-500 text-base after:ms-0.5 after:content-['*']"
                                keypath="signup.fields.termsConditions"
                            >
                                <NuxtLinkLocale class="text-primary-500 font-bold" :to="'terms-and-conditions'" target="_blank">
                                    {{ tt('termsConditions.link') }}
                                </NuxtLinkLocale>
                            </BaseI18nT>
                        </template>
                    </UCheckbox>
                </UFormField>

                <UFormField name="vexatiousClauses">
                    <UCheckbox
                        v-model="formData.vexatiousClauses"
                        class="my-2 md:my-4"
                        name="vexatiousClauses"
                        aria-required="true"
                        :disabled="isSubmitting"
                        @click="trackSignupFieldClick('vexatious_clauses')"
                    >
                        <template #label>
                            <span class="after:text-error-500 text-base after:content-['*']">{{ tt('signup.fields.vexatiousClauses') }}</span>
                            <component
                                :is="vexatiousClausesComponent"
                                class="mt-1 max-h-32 overflow-x-hidden overflow-y-scroll text-base font-normal md:mt-2 md:max-h-40"
                            />
                        </template>
                    </UCheckbox>
                </UFormField>

                <UFormField name="privacy">
                    <UCheckbox
                        v-model="formData.privacy"
                        class="my-2 md:my-4"
                        name="privacy"
                        aria-required="true"
                        :disabled="isSubmitting"
                        @click="trackSignupFieldClick('privacy')"
                    >
                        <template #label>
                            <BaseI18nT
                                tag="span"
                                class="after:text-error-500 text-base after:ms-0.5 after:content-['*']"
                                keypath="signup.fields.privacy"
                            >
                                <NuxtLinkLocale class="text-primary-500 font-bold" to="privacy-policy" target="_blank">
                                    {{ tt('privacy.link') }}
                                </NuxtLinkLocale>
                            </BaseI18nT>
                        </template>
                    </UCheckbox>
                </UFormField>
            </div>

            <UButton :label="tt('signup.createAccount')" type="submit" size="xl" block :loading="isSubmitting" @click="trackSignupCreateClick" />
        </div>
    </UForm>
</template>
